import type { EnvironmentConfig } from '@core/config-models';
import { enc, SHA256 } from 'crypto-js';
import lowerCase from 'lodash/lowerCase';

/**
 * Utility function that fixes a numeric value to a string fixed to 2 digits precision,
 * in line with the analytics specification.
 * @param value the numeric value to cast to an analytics number, defaults to 0
 * @returns string
 */
export const analyticsRoundNumericValue = (value = 0): string =>
  value.toFixed(2);

/**
 * hashes a string in line with the analytics specifications.
 * @param string the string that has to be digested into a hash
 * @returns hexademic sha-256 representation of the string
 */
export const analyticsHash = (string: string): string =>
  enc.Hex.stringify(SHA256(string));

/**
 * Applies a lowercase transformer to all keys of the provided object.
 * @param object object that we want to lowercase the keys on
 * @returns Same object, with all keys following lodash's lowercase spec, see: https://lodash.com/docs/4.17.15#lowerCase
 * @deprecated to be removed Q1 2025, please update your object definitions if they need to be lowercase.
 */
export const lowerCaseObjectKeys = (object: { [key: string]: any }) => {
  return Object.keys(object).reduce(
    (result, key) => {
      const lowerCasedKey = lowerCase(key);
      result[lowerCasedKey] = object[key];
      return result;
    },
    {} as { [key: string]: any }
  );
};

/**
 * @deprecated feature modules should not directly use getSiteContextAbbreviation, to be deleted in Q1 2025.
 * Use `analyticsUtils.getSiteContextAbbreviation()` instead, of you really need this code.
 * @param config environment config.
 * @returns E.G. ESC, ENC, ESZ for ES(sent)C(onsumer), EN(ergiedirect)C(onsument), ES(sent)Z(akelijk)
 */
/* istanbul ignore next */
export const getSiteContextAbbreviation = (config: EnvironmentConfig) => {
  // E.G. ESC, ENC, ESZ for ES(sent)C(onsumer), EN(ergiedirect)C(onsument), ES(sent)Z(akelijk)
  if (
    config.brand != null &&
    config.brand.length >= 2 &&
    config.segment != null &&
    config.segment.length >= 1
  ) {
    const brandAbbreviation = config.brand.substring(0, 2);
    const segmentAbbreviation = config.segment.substring(0, 1);
    return `${brandAbbreviation}${segmentAbbreviation}`.toUpperCase() as
      | 'ESC'
      | 'ESZ'
      | 'ENC';
  }
  return '';
};

/**
 * Processes a value for analytics tracking based on configuration settings.
 *
 * @param value - The original value to process
 * @param config - Configuration options for value processing
 * @param config.sendValue - If false, returns an empty string regardless of input
 * @param config.hashValue - If true, hashes the value for privacy
 * @returns The processed value: empty string, hashed value, or original value
 *
 * @example
 * // Returns original value
 * processAnalyticsValue("user@email.com", { sendValue: true, hashValue: false })
 *
 * // Returns hashed value
 * processAnalyticsValue("user@email.com", { sendValue: true, hashValue: true })
 *
 * // Returns empty string
 * processAnalyticsValue("user@email.com", { sendValue: false })
 */
export function processAnalyticsValue(
  value: string | undefined,
  config: Partial<{ sendValue: boolean; hashValue: boolean }>
) {
  if (!value || !config.sendValue) {
    return '';
  } else if (config.hashValue) {
    return analyticsHash(value);
  } else {
    return value;
  }
}

export const E2E_TRACKING_ID_KEY = 'e2e_tracking_id';
